"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, Database, Droplets, FileText, Gauge, Microscope, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
const processTechnologies = [{
  id: "pat",
  label: "Reaction Monitoring",
  badge: "In-line PAT",
  title: "Real-Time Chemical Reaction & Synthesis Monitoring",
  description: "Continuous spectroscopic tracking of synthesis pathways, kinetic profiles, and reagent conversions directly in chemical reactors and flow chemistry systems.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_process_reactor.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_process_reactor.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_process_reactor.png-wvc-sizes",
  specs: ["In-situ probe options for high pressure and cryogenic vessels", "Quantitative intermediate detection down to trace levels", "Direct OPC-UA connectivity for automated DCS feedback loops", "Data recording aligned with 21 CFR Part 11 requirements"],
  linkTo: "/ProcessAnalyticalTechnologyPage"
}, {
  id: "solvent",
  label: "Solvent Recovery",
  badge: "ReSolve HPLC",
  title: "HPLC Solvent Recycling & Purification Systems",
  description: "Automated distillation and purity verification modules that recover up to 90% of isocratic and gradient mobile phases without baseline disruption.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png-wvc-sizes",
  specs: ["Reduces laboratory hazardous waste volumes significantly", "Optical purity sensors confirm mobile phase consistency", "Zero dead-volume switching valves prevent contamination", "Integrated vapor extraction and containment monitoring"],
  linkTo: "/HplcSolventRecoveryResolvePage"
}, {
  id: "drying",
  label: "Drying & Endpoints",
  badge: "Process Analytics",
  title: "Moisture & Solvent Evaporation Endpoint Detection",
  description: "Non-destructive NIR and photometric sensors that accurately pinpoint drying endpoints in agitated filter dryers and vacuum tray systems.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_sensor.png-wvc-sizes",
  specs: ["Prevents product degradation caused by over-drying cycles", "Eliminates manual thief sampling and exposure risks", "ATEX and IECEx certified probe housings for hazardous zones", "Live moisture curve trending with automated endpoint alarms"],
  linkTo: "/DryingEndpointPage"
}, {
  id: "raw-materials",
  label: "Raw Material ID",
  badge: "Verification",
  title: "Rapid Chemical Identification & Container Release",
  description: "Handheld and benchtop Raman and NIR instrumentation for container identification testing through transparent and opaque packaging.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_testing.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_testing.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_testing.png-wvc-sizes",
  specs: ["Sub-second spectral matching with extensive chemical libraries", "Through-barrier scanning for drums, bags, and glass vials", "Audit trail export ready for LIMS and ERP ingestion", "Factory-calibrated algorithms for polymorphic form detection"],
  linkTo: "/RawMaterialIdentificationPage"
}];
const capabilities = [{
  icon: Gauge,
  title: "Process Analytical Technology",
  desc: "In-line and on-line optical sensors designed for continuous process monitoring and control.",
  link: "/ProcessAnalyticalTechnologyPage"
}, {
  icon: Droplets,
  title: "HPLC Solvent Recovery",
  desc: "Sustainable solvent recycling solutions engineered to lower running costs and disposal burdens.",
  link: "/HplcSolventRecoveryResolvePage"
}, {
  icon: Activity,
  title: "Drying Endpoint Testing",
  desc: "Accurate moisture and residual volatile measurement to optimize cycle times safely.",
  link: "/DryingEndpointPage"
}, {
  icon: ShieldCheck,
  title: "Raw Material ID & Safety",
  desc: "Rapid identification systems ensuring raw substance integrity and regulatory compliance.",
  link: "/RawMaterialIdentificationPage"
}];
export default function Technology() {
  const [activeTab, setActiveTab] = useState("pat");
  return <section id="technologyoverview" data-nav="light" className="bg-background py-16 md:py-24 text-foreground">
      <div className="page-container space-y-16">
        {/* Section Header */}
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4">
            <Sparkles className="size-3 text-primary" />
            Process Analytical Solutions
          </Badge>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Analytical Technology for Regulated Chemical Processes
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed font-default">
            Application-led analytical instrumentation, in-situ process tracking, and qualification support engineered for chemical manufacturing and regulated quality control teams across Ireland and the UK.
          </p>
        </div>

        {/* Interactive Tabs Showcase */}
        <div>
          <Tabs defaultValue="pat" value={activeTab} onValueChange={setActiveTab} className="w-full gap-8">
            <div className="overflow-x-auto pb-2">
              <TabsList className="h-11 w-full justify-start md:w-auto p-1 bg-muted">
                {processTechnologies.map((tech, index) => <TabsTrigger key={tech.id} value={tech.id} data-index={index} className="px-4 py-2 text-sm font-medium transition-[color,box-shadow]">
                    {tech.label}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {processTechnologies.map((tech, index) => <TabsContent key={tech.id} value={tech.id} className="mt-6" data-index={index}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
                  <div className="lg:col-span-7 space-y-6">
                    <div className="space-y-3">
                      <Badge variant="secondary" className="font-medium text-secondary-foreground">
                        {tech.badge}
                      </Badge>
                      <h3 className="text-xl md:text-2xl font-semibold text-foreground font-default">
                        {tech.title}
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed font-default">
                        {tech.description}
                      </p>
                    </div>

                    <div className="space-y-3 pt-2">
                      <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground font-default">
                        Technical Highlights
                      </p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        {tech.specs.map((spec, sIndex) => <li key={sIndex} data-index={sIndex} className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                            <span className="text-foreground leading-snug font-default">{spec}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <Button asChild variant="default">
                        <Link to={tech.linkTo}>
                          Explore Technology
                          <ArrowRight className="size-4 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline">
                        <Link to="/ContactPage">
                          Request Technical Consultation
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <div className="relative overflow-hidden rounded-xl border border-border bg-muted aspect-4/3">
                      <img src={tech.image} data-wvc-srcset={tech.imageSrcset} data-wvc-sizes={tech.imageSizes} alt={tech.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Feature Highlights Grid */}
        <div className="space-y-6 pt-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground font-default">
                Core Process Solutions
              </h3>
              <p className="text-sm text-muted-foreground font-default">
                Modular analytical capabilities for synthesis, recovery, and quality verification.
              </p>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/ProductsPage">
                View all systems
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((cap, index) => {
            const IconComp = cap.icon;
            return <Card key={cap.title} data-index={index} className="group relative border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                      <IconComp className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold font-default">
                      {cap.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-normal font-default">
                      {cap.desc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button asChild variant="link" className="px-0 h-auto font-medium">
                      <Link to={cap.link}>
                        Learn more
                        <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Compliance and Integration Strip */}
        <div className="rounded-xl border border-border bg-secondary/60 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1 max-w-2xl">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary shrink-0" />
              <h4 className="text-base font-semibold text-foreground font-default">
                Regulated Quality & GAMP Lifecycle Support
              </h4>
            </div>
            <p className="text-sm text-muted-foreground font-default">
              Full IQ/OQ qualification protocols, automated data integration, and local technical support across Ireland and the UK.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button asChild variant="default" className="w-full md:w-auto">
              <Link to="/ContactPage">
                Talk to a Specialist
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full md:w-auto">
              <Link to="/ServicesPage">
                View Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}